/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
  message,
  notification,
} from "antd";
import dayjs from "dayjs";
import { bulkEditFields } from "../../utils/bulkEdit";
import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import useMeasure from "react-use-measure";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import usePermissions from "../../hooks/usePermissions";
import { useSavedSearches } from "../../hooks/useSavedSearches";
import dealsService from "../../services/dealsService";
import brokerAccessService from "../../services/brokerAccessService";
import { apiErrorText } from "../../utils/apiError";
import useLookupLabels from "../../hooks/useLookupLabels";
import useDealsOverview from "../../hooks/useDealsOverview";
import { metadataV2ToColumns } from "../../utils/metadataV2Columns";
import {
  DEAL_FILTER_ENTITIES,
  buildDealFilterFields,
  countActiveFilters,
  dealKeysFromQuery,
  filterFieldsToSearchFields,
  lookupValuesFromLabels,
} from "../../utils/filterFields";
import {
  filterBrokerHiddenChildMetadata,
  filterBrokerHiddenFields,
  isExternalBrokerAgent,
  withoutBrokerHiddenRules,
} from "../../utils/brokerHiddenFields";
import { withBrokerRequiredAoi } from "../../utils/brokerRequiredAoi";
import { FILTERS_COLLAPSED, readFlag, writeFlag } from "../../utils/uiPrefs";
import { DEALS_QUERY, readQuery, writeQuery } from "../../utils/stickyQuery";
import { drillIdFrom, takeDrill, withoutDrillParam } from "../../utils/drillHandoff";
import DealsKpiHeader from "../../components/dealsKpiHeader";
import DealsMapBand from "../../components/dealsMapBand";
import DealFormDrawer from "../../components/dealFormDrawer";
import DeleteDealsConfirmModal from "../../components/deleteDealsConfirmModal";

import {
  WigAntLayout,
  WigAntTable,
  WigFilterPanel,
} from "@warwick-investment-group/wig-react-library";

// Ceiling on one export, matching `max_items` on POST /upload/export. A deal
// carries its whole child graph across 16 tabs, so this bounds workbook size —
// it is not a permission. Kept in step with the API deliberately: exceeding it
// is a 422 that reads like a validation bug rather than a limit.
const EXPORT_MAX = 200;

// Every deal fetch on this page is capped at this many rows. It was three
// loose literals; the KPI strip now aggregates whatever the grid is showing,
// so "did we hit the cap" became a thing the page has to answer rather than a
// number buried in three request bodies. A full page means there may be more
// matches than are on screen — the totals above the grid would then be a
// partial sum, and have to say so.
const ROW_CAP = 500;

// Stable identity so the empty-query useMemo / Reset don't churn.
const INITIAL_QUERY = { combinator: "and", rules: [] };

// The antd Table's own vertical furniture, excluded from the measured header
// so `scroll.y` sizes the BODY. See tableScroll.
const TABLE_CHROME = 60;

// Field-level RBAC for the bulk-edit field list comes from `canEditField`
// (utils/permissions), the same predicate the deal edit form uses. It covers
// what the old hardcoded assignment/status sets covered, plus the fields the
// server gates that they missed — notably `operator_notified`, a managed
// select that appears in this list and is restricted to BD.Landman /
// BD.LandTech. UI/UX only; de-hub's bulk-update guard is the real boundary.

// Pull a human-readable message out of an axios error, preferring the FastAPI
// `detail` field (where the 409/422 reason lives). Mirrors dealDetail/index.jsx.
const errText = (err) => apiErrorText(err);

/**
 * BulkEditDealsModal — pick ONE managed-select field and a value to apply
 * across every selected deal.
 *
 * App-specific (deals metadata + RBAC + dealsService.bulkUpdateDeals), so it
 * lives here rather than in wig-react-library — not a promotion candidate.
 *
 * WHICH fields it offers is data: the `bulk_editable` metadata flag (de-hub
 * migration 114, feedback #42), switched per field on Admin -> Field
 * Settings. Of those, only the kinds this dialog can edit are offered: a
 * dropdown with an inline `options` array, or a date. Against a de-hub
 * without the flag (no row carries it) it falls back to the old rule,
 * dropdowns only. The field list is RBAC-gated exactly like the deal edit form (via
 * `canEditField`, which prefers the server's per-caller `editable_for_me`):
 * `brokerage_id` needs `deal.assign`, `agent` / `warwick_landman_id` need
 * `deal.assignPeople`, `op_non_op` needs `deal.editRecordKeeping`, and
 * `deal_status` needs `deal.editStatus`.
 */
const BulkEditDealsModal = ({
  open,
  metadata,
  selectedCount,
  submitting,
  canWriteField,
  onCancel,
  onConfirm,
}) => {
  const [field, setField] = useState(undefined);
  const [value, setValue] = useState(undefined);

  // Reset the picker whenever the modal (re)opens so a prior edit's choice
  // never leaks across opens.
  useEffect(() => {
    if (open) {
      setField(undefined);
      setValue(undefined);
    }
  }, [open]);

  // Fields switched on by `bulk_editable` (data, not code), visible and
  // editable, of a kind the dialog can edit, then RBAC-gated. Async-lookup
  // selects (e.g. brokerage_id via distinct_values_lookup) have no inline
  // `options` and are never offered.
  const fieldOptions = useMemo(
    () => bulkEditFields(metadata, canWriteField),
    [metadata, canWriteField]
  );

  const selectedKind = fieldOptions.find((f) => f.value === field)?.kind;

  const valueOptions = useMemo(() => {
    const sel = fieldOptions.find((f) => f.value === field);
    return (sel?.options || []).map((o) => ({
      value: o.value,
      label: o.label ?? String(o.value),
    }));
  }, [fieldOptions, field]);

  const handleFieldChange = (next) => {
    setField(next);
    // Field changed — the previous value belongs to a different option set.
    setValue(undefined);
  };

  const canConfirm =
    field !== undefined && value !== undefined && !submitting;

  return (
    <Modal
      open={open}
      title={`Bulk edit ${selectedCount} deal${selectedCount === 1 ? "" : "s"}`}
      okText="Apply"
      okButtonProps={{ disabled: !canConfirm, loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      onOk={() => onConfirm({ field, value })}
      onCancel={onCancel}
      destroyOnClose
    >
      {fieldOptions.length === 0 ? (
        <Typography.Text type="secondary">
          No editable fields are available for bulk update.
        </Typography.Text>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div>
            <Typography.Text strong>Field</Typography.Text>
            <Select
              aria-label="Bulk edit field"
              placeholder="Select a field"
              style={{ width: "100%", marginTop: 4 }}
              value={field}
              onChange={handleFieldChange}
              options={fieldOptions.map((f) => ({
                value: f.value,
                label: f.label,
              }))}
            />
          </div>
          <div>
            <Typography.Text strong>New value</Typography.Text>
            {selectedKind === "date" ? (
              <DatePicker
                aria-label="Bulk edit value"
                placeholder="Select a date"
                format="MM/DD/YYYY"
                style={{ width: "100%", marginTop: 4 }}
                value={value ? dayjs(value) : null}
                // Date-only: send the calendar day, never a timestamp.
                onChange={(d) => setValue(d ? d.format("YYYY-MM-DD") : undefined)}
              />
            ) : (
              <Select
                aria-label="Bulk edit value"
                placeholder="Select a value"
                style={{ width: "100%", marginTop: 4 }}
                value={value}
                onChange={setValue}
                disabled={field === undefined}
                options={valueOptions}
              />
            )}
          </div>
        </Space>
      )}
    </Modal>
  );
};

/**
 * Deals list rendered from the redesigned normalized `deals` table and the
 * `/api/broker_deals/metadata` source. Row click navigates to the full-page
 * Deal Detail (no drawer).
 */
const DealsList = () => {
  const [ref, bounds] = useMeasure();
  // Everything above the grid, measured rather than guessed. The old fixed
  // offset was sized for a 6-card KPI block and a filter strip that no longer
  // exist, so it left ~90px of dead space -- and it meant collapsing the KPI
  // row gave the user back page scroll instead of rows.
  const [headerRef, headerBounds] = useMeasure();
  const navigate = useNavigate();
  const { token, getToken, isTokenExpired, user, theme } =
    useContext(AppContext);
  const { can, canEditField, hasRole } = usePermissions();
  const { labelByLookup } = useLookupLabels();
  // Whole-portfolio deals / lease-deeds / tracts / payments, loaded once and
  // shared by the KPI strip and the map band.
  const overview = useDealsOverview();

  // A broker agent is a BD.BrokerAgent WITHOUT any cross-brokerage visibility.
  // `deal.viewAll` is granted to every internal role and (via bypass) to
  // superusers/BD.Admin, so `!can("deal.viewAll")` also excludes a user who
  // holds BrokerAgent alongside an internal role. Purely cosmetic — the server
  // enforces the actual row scoping.
  const isBrokerAgent = isExternalBrokerAgent({ can, hasRole });

  // Saved searches in the filter panel. Only admins may publish one for
  // everybody, and only they may change the shared ones (everyone else sees
  // them read-only). UI only: de-hub enforces both once de-hub#332 is
  // deployed and APP_SHARE_PROTECTED_APPS includes broker-deals.
  const canSaveForAll = can("savedSearches.saveForAll");
  const {
    savedSearches,
    fetchSavedSearches,
    handleSaveSearch,
    handleUpdateSearch,
    handleDeleteSearch,
  } = useSavedSearches({ canSaveForAll });
  const savedSearchUserOid = user?.idTokenClaims?.oid;
  useEffect(() => {
    if (savedSearchUserOid) fetchSavedSearches();
    // Once per signed-in user, not on every token refresh that would
    // re-create fetchSavedSearches. Admin-ness is applied when the list is
    // read (see the hook), so it needs no refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearchUserOid, user?.username]);
  // Defence in depth only: the server sends a broker agent no shared
  // searches (de-hub#332), so this should never find anything. If a saved
  // query does carry a rule on a broker-hidden column, drop it before the
  // panel shows it as a chip, as runSearch does before the request. No-op
  // for internal users.
  const panelSavedSearches = useMemo(
    () =>
      isBrokerAgent
        ? savedSearches.map((s) => ({
            ...s,
            query: withoutBrokerHiddenRules(s.query, true),
          }))
        : savedSearches,
    [savedSearches, isBrokerAgent]
  );

  const [rowData, setRowData] = useState([]);
  const [rawMetadata, setMetadata] = useState([]);
  // Columns an external brokerage must not see at all (WIG's promote
  // economics) drop out of the metadata here, ONCE, rather than at each of the
  // four things fed from it — the grid columns, the search fields, the
  // new-deal form and the bulk-edit field list. Everything below reads
  // `metadata` and inherits the narrowing; a no-op for internal callers.
  // ...and AOI becomes required on the way in, for the same one-place reason:
  // a deal with no AOI is invisible to a broker-lead-tier user, so the server
  // refuses a broker submission without one (see utils/brokerRequiredAoi).
  const metadata = useMemo(
    () =>
      withBrokerRequiredAoi(
        filterBrokerHiddenFields(rawMetadata, "deals", isBrokerAgent),
        isBrokerAgent
      ),
    [rawMetadata, isBrokerAgent]
  );
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [newDealOpen, setNewDealOpen] = useState(false);
  // Filtering runs on the SERVER now (POST /deals/search). `searchQuery` is
  // the applied tree; changing it refetches rather than re-filtering what is
  // already in the browser, so results are no longer capped by the fetch
  // window. The bulk Deal Key filter below still runs client-side.
  // Restored from the tab's session, so opening a deal and coming back does
  // not throw away filtering that took a while to build (feedback #65).
  //
  // A report drill-through opens this list in a NEW tab as `/?drill=<id>`,
  // with the slice's filter waiting in localStorage (utils/drillHandoff).
  // It is read once (and deleted) and wins over anything remembered, since
  // it is what the user just asked to see.
  //
  // Either way, a broker's starting filter loses any rule on a column hidden
  // from them (see withoutBrokerHiddenRules) -- the server would 422 the
  // whole search.
  const [drillQuery] = useState(() =>
    takeDrill(drillIdFrom(window.location.search))
  );
  const [searchQuery, setSearchQuery] = useState(() => {
    const restored = withoutBrokerHiddenRules(
      drillQuery || readQuery(DEALS_QUERY),
      isBrokerAgent
    );
    return restored?.rules?.length ? restored : INITIAL_QUERY;
  });
  // The handoff is one-time: take `?drill=` out of the URL so a reload or a
  // copied link does not look for it again.
  useEffect(() => {
    if (!drillIdFrom(window.location.search)) return;
    navigate(
      `${window.location.pathname}${withoutDrillParam(window.location.search)}${window.location.hash}`,
      { replace: true }
    );
    // Once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // What the mount fetch should ask for. Captured at first render rather than
  // read from `searchQuery`, so `load` keeps its empty dependency list and a
  // later filter change cannot re-trigger the initial fetch.
  const [mountQuery] = useState(searchQuery);
  const [searching, setSearching] = useState(false);

  // Pasted Deal Keys live in the applied query as `deal_key in [...]`, so
  // they filter in the DATABASE like every other condition. They used to be
  // separate client-side state matched against the fetched page, which
  // could not tell a key that does not exist from one outside that page.
  const dealKeys = useMemo(() => dealKeysFromQuery(searchQuery), [searchQuery]);
  const activeFilterCount = useMemo(
    () => countActiveFilters(searchQuery),
    [searchQuery]
  );
  // Metadata for the filterable child tables, keyed by wire prefix. Loaded
  // alongside the deals metadata; a failure here costs the child fields in
  // the picker but not the page.
  const [rawChildMetadata, setChildMetadata] = useState({});
  // Same narrowing as `metadata`, for the child tables: an external brokerage
  // is never offered `lease_deeds.lease_cost` / `wells.allocated_value` in the
  // filter picker. de-hub already omits them; this is the backstop.
  const childMetadata = useMemo(
    () => filterBrokerHiddenChildMetadata(rawChildMetadata, isBrokerAgent),
    [rawChildMetadata, isBrokerAgent]
  );
  // Bulk-edit selection. `selectedRowKeys` drives the controlled antd
  // rowSelection + the button count; `selectedRows` carries the full records
  // so we can read each deal's integer PK (`id`) for the bulk_update body.
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // Bulk delete of the selected deals — same selection state as bulk edit, but
  // behind a typed-confirmation modal. `deleteError` keeps that modal open on
  // failure so the server's reason is readable in place.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // The filter row folds down to a read-only summary of the applied filters.
  // Removing the sider took away a collapse people were using, so it comes
  // back on the row -- and it is remembered, because re-folding it every
  // morning is the kind of small tax that makes a tool feel hostile. Read
  // during useState init so the row renders folded rather than flashing open.
  const [filtersCollapsed, setFiltersCollapsed] = useState(() =>
    readFlag(FILTERS_COLLAPSED)
  );

  const handleFiltersCollapsed = useCallback((next) => {
    setFiltersCollapsed(next);
    writeFlag(FILTERS_COLLAPSED, next);
  }, []);

  // Remounts the filter panel when the applied query is changed from
  // OUTSIDE it (the Deal Key list, or a reset). WigFilterPanel seeds its
  // internal chips from `initialQuery` once, so a new seed alone would not
  // show up -- the key bump is what makes the pasted keys appear as a chip
  // alongside whatever else is set.
  const [panelSeed, setPanelSeed] = useState(0);
  // "Reset filters" (UAT #23): antd keeps column filters/sorters as uncontrolled
  // internal state with no reset API. Toggling the table out of the DOM for one
  // tick forces a full unmount→remount, guaranteeing a fresh instance with every
  // column filter/sorter cleared (a key bump alone did not reliably reset it).
  const [tableMounted, setTableMounted] = useState(true);
  useEffect(() => {
    if (!tableMounted) setTableMounted(true);
  }, [tableMounted]);

  // KPI-follows-filters: the grid's column filters live inside the antd Table
  // (uncontrolled), so we capture the post-filter row set via the Table's
  // `onChange` — `extra.currentDataSource` is the officially-supported view of
  // "rows after filters/sorters" (all pages, not just the current one). We keep
  // only the surviving deal ids; null means "no column filter active".
  const [colFilterIds, setColFilterIds] = useState(null);

  /**
   * Fetch metadata and the grid contents.
   *
   * `query` is the filter to open on — passed by the mount effect when the tab
   * has a remembered filter. It goes into the SAME `Promise.all` in place of
   * the unfiltered list, so restoring a filter costs no extra round trip and
   * the grid never flashes the full set before narrowing. Called with no
   * argument (reset, refresh-after-write) it fetches unfiltered, as before.
   */
  const load = useCallback(async (query) => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
      if (!userOid) throw new Error("OID not found in user claims.");

      let myToken = token;
      if (!myToken || isTokenExpired(myToken)) {
        myToken = await getToken();
      }

      // Child metadata is best-effort: allSettled so one missing table
      // cannot take down the grid, which only needs the deals metadata.
      const childRequests = DEAL_FILTER_ENTITIES.map((e) =>
        dealsService.getMetadata({
          token: myToken,
          userOid,
          tableName: e.name,
        })
      );

      const [metaResp, dealsResp, ...childResults] = await Promise.all([
        dealsService.getDealsMetadata({ token: myToken, userOid }),
        query?.rules?.length
          ? dealsService.searchDeals({
              token: myToken,
              userOid,
              filters: query,
              limit: ROW_CAP,
            })
          : dealsService.listDeals({
              token: myToken,
              userOid,
              filters: { limit: ROW_CAP },
            }),
        ...childRequests.map((pr) => pr.catch(() => null)),
      ]);

      const childMeta = {};
      DEAL_FILTER_ENTITIES.forEach((e, i) => {
        const rows = childResults[i]?.data?.data;
        if (Array.isArray(rows) && rows.length) childMeta[e.name] = rows;
      });
      setChildMetadata(childMeta);

      if (metaResp?.data?.data) {
        setMetadata(metaResp.data.data);
      } else {
        throw new Error("Unexpected response shape from /metadata.");
      }

      if (dealsResp?.data?.data) {
        setRowData(
          Array.isArray(dealsResp.data.data) ? dealsResp.data.data : []
        );
      } else {
        throw new Error("Unexpected response shape from /deals.");
      }
    } catch (err) {
      console.error("Error loading deals:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(mountQuery);
  }, [load, mountQuery]);

  // Broker agents only: ask the server whether this account is linked to a
  // brokerage (drives the "Access pending" empty text below). Best-effort --
  // a failure leaves `myAccess` null, which reads as the normal empty state.
  const [myAccess, setMyAccess] = useState(null);
  useEffect(() => {
    if (!isBrokerAgent) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
        if (!userOid) return;
        let myToken = token;
        if (!myToken || isTokenExpired(myToken)) {
          myToken = await getToken();
        }
        const resp = await brokerAccessService.getMyAccess({
          token: myToken,
          userOid,
        });
        if (!cancelled) setMyAccess(resp?.data?.data ?? null);
      } catch {
        if (!cancelled) setMyAccess(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrokerAgent]);

  // Runs the filter on the server and swaps the grid contents for the result.
  // Kept separate from `load` so a filter change does not refetch metadata.
  const runSearch = useCallback(
    async (rawQuery) => {
      // Every search goes through here, so this is where a rule on a column
      // hidden from a broker is dropped before it reaches the server.
      const query = withoutBrokerHiddenRules(rawQuery, isBrokerAgent);
      try {
        setSearching(true);
        setErrorMsg(null);

        const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
        let myToken = token;
        if (!myToken || isTokenExpired(myToken)) {
          myToken = await getToken();
        }

        const resp = await dealsService.searchDeals({
          token: myToken,
          userOid,
          filters: query,
          limit: ROW_CAP,
        });

        const rows = resp?.data?.data;
        setRowData(Array.isArray(rows) ? rows : []);
        setSearchQuery(query);
        // Remembered only once the server has accepted it, so a query that
        // errored is never the one we restore into (feedback #65).
        writeQuery(DEALS_QUERY, query);
      } catch (err) {
        console.error("Error searching deals:", err);
        setErrorMsg(apiErrorText(err));
      } finally {
        setSearching(false);
      }
    },
    [user, token, isTokenExpired, getToken, isBrokerAgent]
  );

  // Fields for the filter picker: deal columns plus one dotted entry per
  // child column (`wells.spud_date`).
  // FK columns (brokerage_id, warwick_landman_id, aoi_id) store an integer.
  // `useLookupLabels` already resolves those ids to names for the grid cells,
  // so the same map feeds the filter dropdowns -- filtering by "BLACK ROCK
  // LAND LLC" rather than by an id nobody knows.
  const lookupValues = useMemo(
    () => lookupValuesFromLabels(labelByLookup),
    [labelByLookup]
  );

  const filterFields = useMemo(
    () => buildDealFilterFields(metadata, childMetadata, lookupValues),
    [metadata, childMetadata, lookupValues]
  );

  // Advanced is the SAME field list in the older DataItem shape -- FK
  // dropdowns, child-table fields and all. It used to be built from the
  // metadata separately, which is why Brokerage was a dropdown on the chips
  // and a text box in the query builder.
  const advancedFields = useMemo(
    () => filterFieldsToSearchFields(filterFields),
    [filterFields]
  );

  // A full page means the server had more matches than it returned, so
  // anything derived from `rowData` — the KPI totals most of all — is a
  // partial answer and has to say so.
  const atRowCap = rowData.length >= ROW_CAP;

  // Pasted keys not present in the loaded rows — surfaced so the user isn't
  // misled into thinking a deal doesn't exist (it may fall outside the
  // ROW_CAP-row window, or be mistyped).
  /**
   * Pasted keys the search returned nothing for.
   *
   * This is now a real answer. While the key list was matched against the
   * already-fetched page, "not found" conflated "no such deal" with "outside
   * the rows we happen to have" -- the server searches every deal the caller
   * can see, so a key missing from the result really did not match.
   */
  const notFoundDealKeys = useMemo(() => {
    if (!dealKeys || !dealKeys.length) return [];
    const present = new Set(
      rowData.map((r) => String(r?.deal_key ?? r?.DEAL_KEY ?? "").toUpperCase())
    );
    return dealKeys.filter((k) => !present.has(String(k).toUpperCase()));
  }, [dealKeys, rowData]);

  // FK/pick-list columns (brokerage_id, warwick_landman_id, aoi_id, deal_status,
  // …) store a numeric id / code. Map each field to its lookup name once so both
  // the columns and the display rows can resolve id -> label.
  const lookupByField = useMemo(() => {
    const m = {};
    for (const row of metadata) {
      if (row?.distinct_values_lookup) m[row.field] = row.distinct_values_lookup;
    }
    return m;
  }, [metadata]);

  // UAT #22: resolve FK ids -> labels INTO the row data (not just the column
  // `render`). WigAntTable builds its column filter dropdowns AND its CSV export
  // from the raw cell values and ignores `render`, so the id (e.g. 8) leaked
  // through in both. Pre-resolving here makes the grid, its filters, and the
  // export all show the name.
  const displayRows = useMemo(() => {
    const fields = Object.keys(lookupByField);
    // `rowData` arrives already filtered -- the server applied the whole
    // query, deal keys included. Nothing is narrowed in the browser.
    if (fields.length === 0) return rowData;
    return rowData.map((row) => {
      let next = row;
      for (const f of fields) {
        const map = labelByLookup[lookupByField[f]];
        const v = row?.[f];
        if (map && v != null && v !== "" && map[v] != null && map[v] !== v) {
          if (next === row) next = { ...row };
          next[f] = map[v];
        }
      }
      return next;
    });
  }, [rowData, lookupByField, labelByLookup]);

  // The set of deal ids currently visible in the grid after ALL filters
  // (bulk Deal-Key search + antd column filters). null => nothing is filtered,
  // so the KPI header aggregates the full dataset (its original behavior).
  // `displayRows` reflects the parent-level filters and is always current; we
  // intersect with the column-filter survivors so a stale onChange snapshot can
  // only ever narrow, never wrongly re-include a row.
  const visibleDealIds = useMemo(() => {
    // `activeFilterCount` is the search panel (and the bulk Deal-Key paste,
    // which lands in the same query). Without it this gate read
    // `Boolean(colFilterIds)` alone, so the KPI strip followed only the
    // column-header dropdowns — the less-used of the two filter UIs — and
    // ignored the search the comment above claims it reflects. Feedback #75.
    const filtered = Boolean(colFilterIds) || activeFilterCount > 0;
    if (!filtered) return null;
    const ids = displayRows
      .map((r) => r?.id)
      .filter((v) => v != null)
      .filter((id) => !colFilterIds || colFilterIds.has(id));
    return new Set(ids);
  }, [displayRows, colFilterIds, activeFilterCount]);

  // The map band draws from what this page already loaded: the overview the
  // KPI strip uses, the grid's field metadata and its broker names. Without
  // this, opening the map refetched all of it (11 calls).
  const mapData = useMemo(
    () => ({
      tracts: overview.tracts,
      leaseDeeds: overview.leaseDeeds,
      deals: overview.deals,
      dealMetadata: metadata,
      leaseDeedMetadata: childMetadata.lease_deeds || [],
      tractMetadata: childMetadata.lease_deed_tracts || [],
      brokerLabels: labelByLookup?.brokers_xref,
      loading: overview.loading,
      errorMsg: overview.errorMsg,
    }),
    [overview, metadata, childMetadata, labelByLookup]
  );

  // Result summary for the filter row. "shown" is deliberate: the fetch is
  // capped, so this is what is on screen, not what exists.
  //
  // It counts rather than naming the filters. Naming them was worth a line
  // when the chips lived in a collapsible sider; now they sit on the same row
  // as this text, and repeating them here would be the duplication that
  // arrangement was fixed to remove. Column filters are the exception -- they
  // live inside the antd Table and appear as no chip anywhere, so a grid
  // narrowed by one has to say so here or nowhere.
  const gridStatus = useMemo(() => {
    const shown = visibleDealIds ? visibleDealIds.size : displayRows.length;
    const deals = `${shown} deal${shown === 1 ? "" : "s"}`;
    if (loading || searching) return "Loading deals…";
    if (activeFilterCount === 0 && !colFilterIds) return deals;
    if (colFilterIds && atRowCap) {
      return `${deals} shown — column filters only searched the ${ROW_CAP} loaded deals`;
    }
    return colFilterIds
      ? `${deals} shown — column filters applied`
      : `${deals} shown`;
  }, [
    visibleDealIds,
    displayRows.length,
    loading,
    searching,
    activeFilterCount,
    colFilterIds,
    atRowCap,
  ]);

  // Column filters run in the browser over the rows the server returned; the
  // filter panel runs in the database. Below the cap the two agree. At the cap
  // a column filter is answering over a partial set and looks no different,
  // so that is the one moment the difference is worth explaining.
  const columnFiltersCapped = Boolean(colFilterIds) && atRowCap;

  const canEditDeal = can("deal.editTerms");
  // Bulk Edit is open to every role POST /deals/bulk_update admits (feedback
  // #42); what each may change is still per field, via canWriteField.
  const canBulkEdit = can("deal.bulkEdit");
  // Per-field write predicate for the bulk-edit field list — prefers the
  // server's `editable_for_me` on each metadata row.
  const canWriteField = useCallback(
    (field, metaRow) => canEditField(field, metaRow),
    [canEditField]
  );
  // Delete is open to the deal-maintenance roles (see utils/permissions.js);
  // UI/UX gating only — de-hub is the real boundary.
  const canDeleteDeal = can("deal.delete");

  // Controlled multi-select for the grid. WigAntTable spreads unknown props
  // (incl. `rowSelection`) onto the underlying antd Table (verified against the
  // component source), so this drives real row checkboxes. onChange hands back
  // both the keys (for the count / controlled state) and the full row records
  // (for the integer `id` the bulk_update endpoint keys on).
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys, rows) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
      },
    }),
    [selectedRowKeys]
  );

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  /**
   * The ONE reset. Clears the filter chips, the pasted Deal Keys (now part
   * of the same query) AND the grid's own column filters / sorters, which
   * antd keeps as uncontrolled internal state -- hence the remount.
   *
   * Previously the panel and the table each had their own reset and neither
   * cleared the other, so 'reset' left filtering behind either way.
   */
  const handleFilterReset = useCallback(() => {
    setSearchQuery(INITIAL_QUERY);
    // Reset means reset: the remembered filter has to go too, or the next
    // visit to this tab's deals list quietly restores what was just cleared.
    writeQuery(DEALS_QUERY, null);
    setPanelSeed((k) => k + 1);
    setColFilterIds(null); // KPIs revert to the full dataset
    clearSelection();
    setTableMounted(false); // remount clears column filters/sorters
    load();
  }, [load, clearSelection]);

  /**
   * Reload the grid, PRESERVING any filter the user has applied.
   *
   * Callers after a create / bulk-edit / bulk-delete want "show me the current
   * data", not "throw my filter away" -- plain `load()` refetches unfiltered,
   * which would silently widen the grid back out under them.
   */
  const refresh = useCallback(() => {
    if (searchQuery?.rules?.length) return runSearch(searchQuery);
    return load();
  }, [searchQuery, runSearch, load]);


  // On successful create, close the drawer and jump straight to the new
  // deal's detail page (keyed by its natural key).
  const onDealCreated = useCallback(
    ({ deal } = {}) => {
      setNewDealOpen(false);
      const key = deal?.deal_key;
      if (key != null && key !== "") {
        navigate(`/deals/${encodeURIComponent(key)}`);
      } else {
        // No key in the response — fall back to refreshing the list.
        refresh();
      }
    },
    [navigate, refresh]
  );

  const handleTableChange = useCallback((_pagination, filters, _sorter, extra) => {
    const anyFilter =
      filters &&
      Object.values(filters).some((v) =>
        Array.isArray(v) ? v.length > 0 : v != null
      );
    if (!anyFilter) {
      setColFilterIds(null);
      return;
    }
    const rows = extra?.currentDataSource || [];
    setColFilterIds(new Set(rows.map((r) => r?.id).filter((v) => v != null)));
  }, []);


  // Apply one field/value across every selected deal via the bulk_update
  // endpoint. On success: toast the count, warn on any skipped ids, refresh the
  // grid, clear the selection, close the modal. Errors surface via message.
  const handleBulkConfirm = useCallback(
    async ({ field, value }) => {
      setBulkSubmitting(true);
      try {
        const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
        if (!userOid) throw new Error("OID not found in user claims.");

        let myToken = token;
        if (!myToken || isTokenExpired(myToken)) {
          myToken = await getToken();
        }

        // The endpoint keys on the integer PK (`deals.id`). Pull it off the
        // selected records; drop any row missing an id defensively.
        const dealIds = selectedRows
          .map((r) => r?.id)
          .filter((v) => v != null);

        if (dealIds.length === 0) {
          message.warning("Selected deals have no id — nothing to update.");
          return;
        }

        const resp = await dealsService.bulkUpdateDeals({
          token: myToken,
          userOid,
          dealIds,
          updates: { [field]: value },
        });

        const payload = resp?.data?.data ?? resp?.data ?? {};
        const updated = payload.updated ?? 0;
        const skipped = Array.isArray(payload.skipped) ? payload.skipped : [];

        message.success(`Updated ${updated} deal${updated === 1 ? "" : "s"}`);
        if (skipped.length) {
          notification.warning({
            message: `${skipped.length} deal${
              skipped.length === 1 ? "" : "s"
            } skipped`,
            description: skipped
              .map((s) => `#${s.deal_id}: ${s.reason || "skipped"}`)
              .join("\n"),
          });
        }

        setBulkOpen(false);
        clearSelection();
        await refresh();
      } catch (err) {
        console.error("Error bulk-updating deals:", err);
        message.error(`Bulk update failed: ${errText(err)}`);
      } finally {
        setBulkSubmitting(false);
      }
    },
    [
      token,
      getToken,
      isTokenExpired,
      user,
      selectedRows,
      clearSelection,
      refresh,
    ]
  );

  // The deal keys behind the current selection — shown in the confirm modal so
  // the user sees exactly which deals go. Falls back to the integer id for a
  // row with no natural key (shouldn't happen; the grid keys on deal_key).
  const selectedDealKeys = useMemo(
    () =>
      selectedRows
        .map((r) => r?.deal_key ?? r?.DEAL_KEY ?? r?.id)
        .filter((k) => k != null && k !== "")
        .map(String),
    [selectedRows]
  );

  // Delete every selected deal through the one bulk endpoint (the server does
  // the per-deal audit + cascade cleanup in a single transaction — never loop
  // the single-deal DELETE from here). Partial failures come back per-deal and
  // are surfaced instead of being swallowed by the success toast.
  const handleBulkDeleteConfirm = useCallback(async () => {
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
      if (!userOid) throw new Error("OID not found in user claims.");

      let myToken = token;
      if (!myToken || isTokenExpired(myToken)) {
        myToken = await getToken();
      }

      // Keyed on the integer PK (`deals.id`), like bulk_update.
      const dealIds = selectedRows.map((r) => r?.id).filter((v) => v != null);
      if (dealIds.length === 0) {
        setDeleteError("Selected deals have no id — nothing to delete.");
        return;
      }
      // deal_id -> deal_key, so a per-deal failure names the deal the user
      // recognizes rather than a bare PK.
      const keyById = new Map(
        selectedRows
          .filter((r) => r?.id != null)
          .map((r) => [r.id, r?.deal_key ?? r?.DEAL_KEY ?? `#${r.id}`])
      );

      const resp = await dealsService.bulkDeleteDeals({
        token: myToken,
        userOid,
        dealIds,
      });

      const payload = resp?.data?.data ?? resp?.data ?? {};
      // The endpoint is landing concurrently in de-hub; read the per-deal
      // failure report defensively (documented as `skipped`, mirroring
      // bulk_update, but tolerate `failed`/`errors`) and never assume a clean
      // success just because the call resolved.
      const failures =
        [payload.skipped, payload.failed, payload.errors].find(Array.isArray) ||
        [];
      const deleted =
        typeof payload.deleted === "number"
          ? payload.deleted
          : Math.max(dealIds.length - failures.length, 0);

      if (deleted > 0) {
        message.success(`Deleted ${deleted} deal${deleted === 1 ? "" : "s"}`);
      }
      if (failures.length) {
        notification.warning({
          message: `${failures.length} deal${
            failures.length === 1 ? "" : "s"
          } could not be deleted`,
          description: failures
            .map((f) => {
              const id = f?.deal_id ?? f?.id;
              const label = keyById.get(id) ?? (id != null ? `#${id}` : "deal");
              return `${label}: ${f?.reason || f?.error || "not deleted"}`;
            })
            .join("\n"),
          // Sticky — a partial failure is something the user has to act on.
          duration: 0,
        });
      }

      // Cached GETs never self-invalidate on a mutation, so drop them before
      // re-fetching or the reload hands back the deleted rows.
      await dealsService.invalidateCache?.();

      setDeleteOpen(false);
      clearSelection();
      await refresh();
    } catch (err) {
      console.error("Error deleting deals:", err);
      const status = err?.response?.status;
      setDeleteError(
        // 404/405 here means the bulk_delete route isn't deployed on this
        // server yet, NOT that the deals are gone — say so instead of letting
        // apiErrorText's "that record no longer exists" mislead the user.
        status === 404 || status === 405
          ? "Bulk delete isn't available on this server yet. Open a deal and delete it from its own page."
          : errText(err)
      );
    } finally {
      setDeleteSubmitting(false);
    }
  }, [
    token,
    getToken,
    isTokenExpired,
    user,
    selectedRows,
    clearSelection,
    refresh,
  ]);

  // Export the selected deals INTO the upload workbook — the populated
  // counterpart to the blank "Download template" on the Upload page. Comes back
  // as a binary .xlsx, so the response is blob-typed and errors carry their
  // JSON body inside the blob (hence `parseBlobError` before reading `detail`).
  //
  // Keyed on the integer PK (`deals.id`), like bulk_update / bulk_delete — the
  // route takes ids, not deal_keys.
  const handleExportSelected = useCallback(async () => {
    // The API rejects more than EXPORT_MAX ids. Refuse here rather than
    // spending a round trip to be told: a deal drags its whole child graph
    // across 16 tabs, so the cap is about workbook size, not permission.
    if (selectedRows.length > EXPORT_MAX) {
      message.warning(
        `Export is limited to ${EXPORT_MAX} deals at a time — ` +
          `${selectedRows.length} are selected.`
      );
      return;
    }
    const dealIds = selectedRows.map((r) => r?.id).filter((v) => v != null);
    if (dealIds.length === 0) {
      message.warning("No exportable deals in the selection.");
      return;
    }
    try {
      setExporting(true);
      const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
      if (!userOid) throw new Error("OID not found in user claims.");
      let myToken = token;
      if (!myToken || isTokenExpired(myToken)) {
        myToken = await getToken();
      }
      const resp = await dealsService.exportDealsToTemplate({
        token: myToken,
        userOid,
        dealIds,
      });
      const name = dealsService.saveBlobResponse(resp, "BDA_Deal_Export.xlsx");
      message.success(
        `Exported ${dealIds.length} deal${dealIds.length === 1 ? "" : "s"} to ${name}`
      );
    } catch (rawErr) {
      const err = await dealsService.parseBlobError(rawErr);
      console.error("Error exporting deals:", err);
      const status = err?.response?.status;
      message.error(
        // 404/405 means the export route isn't deployed on this server yet,
        // NOT that the deals are missing — apiErrorText's "no longer exists"
        // would be actively misleading here. Mirrors the bulk-delete guard.
        status === 404 || status === 405
          ? "Deal export isn't available on this server yet."
          : errText(err)
      );
    } finally {
      setExporting(false);
    }
  }, [token, getToken, isTokenExpired, user, selectedRows]);

  const columns = useMemo(() => {
    // FK ids are already resolved to labels in `displayRows` (UAT #22), so the
    // columns need no per-cell label render here.
    const base = metadataV2ToColumns(metadata);
    // Role-aware row affordance: users who can edit a deal get a pencil
    // "Edit"; everyone else gets an eye "View". Both navigate to the detail
    // page (editing happens in-place there, and the detail page enforces its
    // own gating — this is UI/UX only).
    const verb = canEditDeal ? "Edit" : "View";
    // The Deal Key is the deal's natural identifier and the primary thing the
    // land team searches/filters on, but the backend metadata hides it. Inject
    // a pinned "Deal Key" column (guarded so we never duplicate it if the
    // metadata ever exposes it). Falls back to the DEAL_KEY casing.
    const hasDealKey = base.some((c) => c.dataIndex === "deal_key");
    const dealKeyCol = hasDealKey
      ? []
      : [
          {
            title: "Deal Key",
            dataIndex: "deal_key",
            key: "deal_key",
            fixed: "left",
            width: 140,
            sorter: (a, b) =>
              String(a.deal_key ?? a.DEAL_KEY ?? "").localeCompare(
                String(b.deal_key ?? b.DEAL_KEY ?? "")
              ),
            render: (val, record) => val ?? record?.DEAL_KEY ?? "",
          },
        ];
    return [
      {
        title: "",
        key: "_open",
        fixed: "left",
        width: 56,
        align: "center",
        render: (_, record) => {
          const key = record?.deal_key ?? record?.DEAL_KEY;
          const disabled = key == null || key === "";
          const to = disabled ? undefined : `/deals/${encodeURIComponent(key)}`;
          return (
            <Button
              size="small"
              type="link"
              icon={canEditDeal ? <EditOutlined /> : <EyeOutlined />}
              disabled={disabled}
              // A real `href`, so the browser's own "open in new tab" works:
              // ctrl/cmd-click, middle-click, and the context menu (feedback
              // #65). antd renders an <a> once `href` is set. A plain click
              // still routes in-app -- preventDefault below -- so this costs
              // no full page reload.
              href={to}
              onClick={(e) => {
                if (disabled) return;
                // Let the browser handle any click that asks for a new tab or
                // window. Checking `button` as well as the modifiers keeps a
                // middle-click working in the browsers that route it here.
                if (
                  e.metaKey ||
                  e.ctrlKey ||
                  e.shiftKey ||
                  e.altKey ||
                  e.button !== 0
                ) {
                  return;
                }
                e.preventDefault();
                navigate(to);
              }}
              aria-label={disabled ? `${verb} deal` : `${verb} deal ${key}`}
            />
          );
        },
      },
      ...dealKeyCol,
      ...base,
    ];
  }, [metadata, navigate, canEditDeal]);

  // Reserve room for the table chrome: search/title row (~50), column
  // header (~55), pagination (~60), export footer (~45), padding (~50).
  const tableScroll = useMemo(
    () => ({
      x: "max-content",
      // `TABLE_CHROME` is the table's own furniture (header row, pagination,
      // padding) -- the one part that cannot be measured from here. Derived
      // from the offset this used to hard-code (280) minus the header block it
      // assumed (~220), so the arithmetic starts where the working version
      // left off. Worth eyeballing in a browser at one narrow and one wide
      // viewport; everything else now adapts on its own.
      y: Math.max(bounds.height - headerBounds.height - TABLE_CHROME, 260),
    }),
    [bounds.height, headerBounds.height]
  );

  // A broker agent with no brokerage mapping gets zero deals from the server
  // (fail-closed). When that's the case, swap the generic "no data" for a
  // friendly "access pending" prompt so they know to contact the land team.
  // The decision comes from GET /my-access, NOT from an empty grid: rows are
  // server-filtered, so a mapped broker whose search matches nothing (or whose
  // brokerage has no deals yet) also sees zero rows. Only an explicit
  // `mapped === false` shows the prompt; loading or a failed call falls back
  // to the normal empty state.
  const tableLocale = useMemo(() => {
    if (isBrokerAgent && myAccess?.mapped === false) {
      return {
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Access pending — your account isn't linked to a brokerage yet. Contact the land team."
          />
        ),
      };
    }
    return undefined;
  }, [isBrokerAgent, myAccess]);

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          {/* No `menu` prop, so WigAntLayout renders no Sider at all
              (`showSider = showDefaultMenu || menu`). The 340px filter column
              is gone and the grid has that width back -- on a 121-column table
              that was roughly a quarter of the viewport spent on four controls
              and two chips, for the whole session. Filtering now lives on one
              row above the grid; see the WigFilterPanel below. */}
          <WigAntLayout
            darkMode={theme === "dark"}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 0,
              paddingLeft: 10,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            showDefaultMenu={false}
            content={
              <>
                <div ref={headerRef}>
                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ width: "99%", marginBottom: 8 }}
                    message="Couldn't load deals"
                    description={errorMsg}
                    action={
                      <Button size="small" onClick={refresh}>
                        Retry
                      </Button>
                    }
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    width: "99%",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "8px 0",
                  }}
                >
                  {/* The page has no chrome header, so this is the only thing
                      naming the screen. */}
                  <Typography.Text strong style={{ fontSize: 16 }}>
                    Deals
                  </Typography.Text>
                  {/* The bar is no longer gated on edit/delete/create: export
                      is open to every BD role (the route takes ALL_BD), so a
                      read-only user still gets it. The write actions stay
                      individually gated inside. */}
                  <Space>
                    <Button
                      icon={<ExportOutlined />}
                      loading={exporting}
                      disabled={selectedRowKeys.length === 0}
                      onClick={handleExportSelected}
                      data-testid="export-deals"
                    >
                      Export to template ({selectedRowKeys.length})
                    </Button>
                    {canBulkEdit && (
                        <Button
                          icon={<EditOutlined />}
                          disabled={selectedRowKeys.length === 0}
                          onClick={() => setBulkOpen(true)}
                        >
                          Bulk edit ({selectedRowKeys.length})
                        </Button>
                      )}
                      {canDeleteDeal && (
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          disabled={selectedRowKeys.length === 0}
                          onClick={() => {
                            setDeleteError(null);
                            setDeleteOpen(true);
                          }}
                        >
                          Delete ({selectedRowKeys.length})
                        </Button>
                      )}
                      {can("deal.create") && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => setNewDealOpen(true)}
                        >
                          New Deal
                        </Button>
                      )}
                  </Space>
                </div>
                {notFoundDealKeys.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ width: "99%", marginBottom: 8 }}
                    message={
                      `${notFoundDealKeys.length} of ${dealKeys.length} pasted Deal Key` +
                      `${dealKeys.length === 1 ? "" : "s"} matched no deal` +
                      `${atRowCap ? ` (results are capped at ${ROW_CAP})` : ""}: ` +
                      `${notFoundDealKeys.slice(0, 10).join(", ")}` +
                      `${notFoundDealKeys.length > 10 ? "…" : ""}`
                    }
                  />
                )}
                <DealsKpiHeader
                  overview={overview}
                  visibleDealIds={visibleDealIds}
                  atRowCap={atRowCap}
                />
                {/* The whole filtering surface, on one row, directly above the
                    rows it filters: add / apply / clear, then the chips, then
                    saved searches and the Simple|Advanced switch. The query
                    builder opens in a drawer off the Advanced segment, which
                    is the only part of this that ever needed a big surface.

                    `status` puts the result count on the panel's own hint
                    line, so "what is filtered", "is it applied yet" and "what
                    came back" are one block instead of three bands. There is
                    now exactly ONE reset control on the page: `resetLabel`
                    renames the panel's Reset to what this app's handler
                    actually does, which also clears the grid's column filters
                    and sorting. */}
                <div style={{ width: "99%", padding: "4px 0" }}>
                  <WigFilterPanel
                    key={panelSeed}
                    layout="inline"
                    fields={filterFields}
                    advancedFields={advancedFields}
                    entities={DEAL_FILTER_ENTITIES}
                    initialQuery={searchQuery}
                    onSearch={runSearch}
                    onReset={handleFilterReset}
                    enableSavedSearches
                    savedSearches={panelSavedSearches}
                    onSaveSearch={handleSaveSearch}
                    onUpdateSearch={handleUpdateSearch}
                    onDeleteSearch={handleDeleteSearch}
                    allowSaveForAll={canSaveForAll}
                    collapsible
                    collapsed={filtersCollapsed}
                    onCollapsedChange={handleFiltersCollapsed}
                    resetLabel="Clear all"
                    resetTooltip="Clears the filter chips and the grid's column filters and sorting"
                    status={
                      <Tooltip
                        title={
                          columnFiltersCapped
                            ? `Column filters narrow the deals already loaded, and the grid loads at most ${ROW_CAP}. Add a filter here to search every deal.`
                            : undefined
                        }
                      >
                        <Typography.Text
                          type={columnFiltersCapped ? "warning" : "secondary"}
                          style={{ whiteSpace: "nowrap" }}
                          data-testid="grid-status"
                        >
                          {gridStatus}
                        </Typography.Text>
                      </Tooltip>
                    }
                  />
                </div>
                {/* Filter -> map -> rows. Inside the measured header, so
                    opening it shortens the grid rather than the page. Shown
                    to every role: what it draws is scoped by the server. */}
                <DealsMapBand
                  visibleDealIds={visibleDealIds}
                  atRowCap={atRowCap}
                  rowCap={ROW_CAP}
                  data={mapData}
                />
                </div>
                {tableMounted && (
                  <WigAntTable
                    dataSource={displayRows}
                    columns={columns}
                    loading={loading || searching}
                    tableWidth="99%"
                    scroll={tableScroll}
                    showSearch={false}
                    size="small"
                    exportOptions={{ filename: "broker_deals" }}
                    pagination={{
                      defaultPageSize: 100,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    locale={tableLocale}
                    onChange={handleTableChange}
                    rowSelection={rowSelection}
                    rowKey={(record) =>
                      record?.deal_key ??
                      record?.DEAL_KEY ??
                      record?.id ??
                      Math.random().toString(36)
                    }
                  />
                )}
                <DealFormDrawer
                  open={newDealOpen}
                  mode="create"
                  metadata={metadata}
                  onClose={() => setNewDealOpen(false)}
                  onSaved={onDealCreated}
                />
                <BulkEditDealsModal
                  open={bulkOpen}
                  metadata={metadata}
                  selectedCount={selectedRowKeys.length}
                  submitting={bulkSubmitting}
                  canWriteField={canWriteField}
                  onCancel={() => setBulkOpen(false)}
                  onConfirm={handleBulkConfirm}
                />
                <DeleteDealsConfirmModal
                  open={deleteOpen}
                  dealKeys={selectedDealKeys}
                  submitting={deleteSubmitting}
                  errorMsg={deleteError}
                  onCancel={() => setDeleteOpen(false)}
                  onConfirm={handleBulkDeleteConfirm}
                />
              </>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default DealsList;
